import { useMemo, useState } from "react";
import { Card } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import BudgetControlGraphFilter from "./BudgetControlGraphFilter";
import BudgetControlGraphItem from "./BudgetControlGraphItem";
import BudgetControlGraphView, { DataItem } from "./BudgetControlGraphView";
import {
  colors,
  spacing,
} from "../../../../../../../../../assets/styles/tokens";
import { Box } from "@mui/system";
import BudgetControlGraphPagination from "./BudgetControlGraphPagination";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import { DEFAULT_BUDGET_SORT, sortBudgetRows } from "../../helpers/budgetSort";
import { setBrandNameForBudget } from "../../../../../../../../../features/bizDevelopmentDashboardSlice";

const PAGE_SIZE = 5;

// Bar palette — preserves the designed multi-colour look, cycled per page
const BAR_COLORS = ["#EA6A00", "#3B9EF5", "#8B6FF5", "#F87171", "#38BDF8"];

const cleanNumber = (value: any): number =>
  parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

// Show at most 7 letters of a brand name, then append "..." for better UX
const truncateName = (text: string, maxChars = 7): string => {
  const str = String(text ?? "");
  if (str.length <= maxChars) return str;
  return `${str.slice(0, maxChars)}...`;
};

const BudgetControlGraph = () => {
  const budgetData: any[] = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile.budgetMobileDataOfBizDevelopment ??
      [],
  );

  const { fetchBudgetData } = useBizDevelopmentDashboardMobile();

  const [overuse, setOveruse] = useState(false);
  const [page, setPage] = useState(0); // 0-based
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState(DEFAULT_BUDGET_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_BUDGET_SORT.dir,
  );

  const dispatch = useDispatch();
  // Fetched brand name from Redux
  const brandNameForBudget = useSelector(
    (state: any) => state.bizDevelopmentDashboard.brandNameForBudget,
  );

  // Unique brand names for the search Autocomplete
  const brandNames = useMemo(() => {
    const set = new Set<string>();
    (budgetData || []).forEach((r: any) => {
      if (typeof r?.brand_display_name === "string")
        set.add(r.brand_display_name.trim());
    });
    return Array.from(set);
  }, [budgetData]);

  // Budget overuse filter — graph view only considers perc_of_budget_used (mirrors desktop)
  const filteredData = useMemo(() => {
    const search = (brandNameForBudget ?? "").trim().toLowerCase();
    let data = budgetData;

    if (search) {
      data = data.filter((row: any) =>
        String(row["brand_display_name"] ?? "")
          .toLowerCase()
          .includes(search),
      );
    }

    if (overuse) {
      data = data.filter(
        (row: any) => cleanNumber(row["perc_of_budget_used"]) * 100 >= 100,
      );
    }

    return data;
  }, [budgetData, overuse, brandNameForBudget]);

  // Sort rows by the selected metric/direction (all budget metrics supported)
  const sortedData = useMemo(
    () => sortBudgetRows(filteredData, sortKey, sortDir),
    [filteredData, sortKey, sortDir],
  );

  const totalPages = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);

  const pagedData = useMemo(
    () =>
      sortedData.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [sortedData, safePage],
  );

  // Chart bars: value = perc_of_budget_used × 100 (mirrors desktop chart mapping)
  const chartData: DataItem[] = useMemo(
    () =>
      pagedData.map((row: any, index: number) => {
        const color = BAR_COLORS[index % BAR_COLORS.length];
        const fullName = row["brand_display_name"] ?? "";
        return {
          name: truncateName(fullName), // X-axis label — truncated for better UX
          shortName: fullName, // legend — full brand name
          value: cleanNumber(row["perc_of_budget_used"]) * 100,
          color,
          activeColor: color,
        };
      }),
    [pagedData],
  );

  const activeIndex = selectedBrand
    ? pagedData.findIndex(
        (row: any) => row["brand_display_name"] === selectedBrand,
      )
    : -1;

  const selectedRow =
    selectedBrand && activeIndex >= 0 ? pagedData[activeIndex] : null;

  const handleSelect = (index: number) => {
    const brand = pagedData[index]?.["brand_display_name"] ?? null;
    // Toggle off when the active bar is tapped again (matches desktop)
    setSelectedBrand((prev) => (prev === brand ? null : brand));
  };

  const handleOveruseToggle = (checked: boolean) => {
    setOveruse(checked);
    setPage(0);
    setSelectedBrand(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedBrand(null); // selection is relative to the visible page
  };

  // Apply a new sort — reset to the first page and clear the selection
  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
    setPage(0);
    setSelectedBrand(null); // order changed → clear selection
  };

  // Refresh — re-fetch budget data (mirrors desktop)
  const handleRefresh = () => {
    fetchBudgetData();
  };

  const handleBrandNameChange = (value: string) => {
    dispatch(setBrandNameForBudget(value));
    setPage(0);
    setSelectedBrand(null);
  };

  const handleDownloadCSV = () => {
    if (sortedData.length === 0) return;
    const headers = "Brand,% Budget Used";
    const csvRows = sortedData.map((row: any) => {
      const brand = row["brand_display_name"] ?? "";
      const perc =
        row["perc_of_budget_used"] != null
          ? `${Math.round(row["perc_of_budget_used"] * 100)}%`
          : "";
      const escapedBrand = brand.includes(",") ? `"${brand}"` : brand;
      return `${escapedBrand},${perc}`;
    });
    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `budget_control_graph_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <BudgetControlGraphFilter
        brandCount={filteredData.length}
        overuse={overuse}
        onOveruseToggle={handleOveruseToggle}
        onRefresh={handleRefresh}
        onDownload={handleDownloadCSV}
        sortKey={sortKey}
        sortDir={sortDir}
        onApplySort={handleApplySort}
        brandNames={brandNames}
        brandName={brandNameForBudget}
        onBrandNameChange={handleBrandNameChange}
      />
      <Box sx={{ mt: "-" + spacing.Spacing.md.$value }}>
        <Card
          sx={{
            p: 1,
            borderRadius: spacing.Radius.lg.$value,
            boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
            border: "1px solid" + colors.GrayLightMode[200].$value,
            mb: spacing.Spacing.none.$value,
            mt: spacing.Spacing.xl.$value + "!important",
          }}
        >
          <BudgetControlGraphView
            data={chartData}
            activeIndex={activeIndex >= 0 ? activeIndex : null}
            onSelect={handleSelect}
          />
          <BudgetControlGraphItem selectedRow={selectedRow} />
        </Card>
      </Box>
      <BudgetControlGraphPagination
        total={filteredData.length}
        pageSize={PAGE_SIZE}
        page={safePage}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default BudgetControlGraph;
